import { keyBy } from 'lodash-es';
import Posts from '../../../services/post-loader';

import { getSocialPosts } from '../../../utils/production';
import { postWithEmojis } from '../../../utils/emojis';

import { sleep } from '../../../utils/helpers';

const state = {
  postModalShown: false,
  postModalPost: null,
  lookupTable: {},
  byTagboard: {},
  isLoading: {},

  // Track which posts are being loaded by post_id
  isLoadingMap: {},

  // Track which posts are confirmed missing (deleted/unfeatured) by post_id
  notFoundMap: {},
};

const findAllByKey = (obj, key) => {
  if (!obj) {
    return [];
  }

  return Object.entries(obj)
    .reduce((memo, [k, value]) => {
      let ret = memo;

      if (k === key) {
        ret = ret.concat(value);
      }

      if (typeof value !== 'object') {
        return ret;
      }

      return ret.concat(findAllByKey(value, key));
    }, []);
};

const actions = {
  async getPostsByTagboard(context, opts = {}) {
    const {
      tagboardId,
      count = 50,
      before = null,
    } = opts;

    context.commit('SET_IS_LOADING_POSTS', {
      key: tagboardId,
      value: true,
    });

    let data;

    try {
      data = await Posts.loadFeaturedPosts(tagboardId, count, before);
      data.posts = data.posts.map(postWithEmojis);
      context.commit('ADD_TAGBOARD_POSTS', {
        tagboardId,
        posts: data.posts,
      });
    } catch (err) {
      console.error('Failed to load featured posts for tagboard', tagboardId, err);
    } finally {
      context.commit('SET_IS_LOADING_POSTS', {
        key: tagboardId,
        value: false,
      });
    }

    return data;
  },

  /**
   * Loop over existing timeline items and fetch any posts found
   * in either themes or social graphics
   */
  async getTimelinePosts(context, opts = {}) {
    const {
      retries = 0,
      force = false,
    } = opts;

    const { timeline } = context.rootState.production.production;

    // Kinda lazy, but we just look for any field named `posts`
    const posts = findAllByKey(timeline, 'posts');

    // Then we narrow those down to ACTUAL social posts, since it could be anything
    // (ignoring any duplicates)
    const socialPosts = posts.reduce((memo, post) => {
      // Only care if post has a post_id and network.
      if (!post?.post_id || !post?.network) { return memo; }

      // Exclude posts that are already loaded, unless force === true
      if (!force && state.lookupTable[post.post_id]) { return memo; }

      // Exclude posts already confirmed missing, unless force === true
      if (!force && state.notFoundMap[post.post_id]) { return memo; }

      // Exclude posts already in list of posts to fetch, in case there are duplicates
      // in the timeline
      if (memo.find((val) => val.post_id === post.post_id && val.network === post.network)) {
        return memo;
      }

      return memo.concat({
        post_id: post.post_id,
        network: post.network,
      });
    }, []);

    if (!socialPosts?.length) { return; }

    const postIds = socialPosts.map((post) => post.post_id);
    context.commit('SET_POSTS_LOADING', { ids: postIds, loading: true });

    const { owner } = context.rootState.production.production;

    try {
      const data = await Posts.getManyPosts(owner, socialPosts);

      // NOTE: We might want to implement better error handling here for when _some_ posts aren't returned in the resp
      if (data?.results?.length) {
        context.commit('ADD_POSTS_TO_LOOKUP', data.results);
      }

      // Any post requested but missing from the response is presumed
      // deleted/unfeatured. Only on successful responses, so network errors
      // don't incorrectly mark posts as missing.
      const returnedIds = new Set((data?.results || []).map((post) => post.post_id));
      const notFoundIds = postIds.filter((id) => !returnedIds.has(id));
      if (notFoundIds.length) {
        context.commit('SET_POSTS_NOT_FOUND', { ids: notFoundIds, notFound: true });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error loading social posts:', err);

      if (retries <= 3) {
        await sleep(500);
        await context.dispatch('getTimelinePosts', { ...opts, retries: retries + 1 });
      } else {
        throw err;
      }
    } finally {
      context.commit('SET_POSTS_LOADING', { ids: postIds, loading: false });
    }
  },

  /**
   * Add a single post to lookupTable
   */
  addToPostsLookupTable(context, post) {
    context.commit('ADD_POST_TO_LOOKUP', post);
  },

  toggleModal(context) {
    context.commit('SET_POST_MODAL_SHOWN', true);
  },

  setModalPost(context, payload) {
    context.commit('SET_POST_MODAL_POST', payload);
  },

  clearModalPost(context) {
    context.commit('CLEAR_POST_MODAL_POST');
  },

  hideModal(context) {
    context.commit('SET_POST_MODAL_SHOWN', false);
  },

  clearTagboardPosts(context, tagboardId) {
    context.commit('CLEAR_TAGBOARD_POSTS', tagboardId);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  ADD_POSTS_TO_LOOKUP(state, posts) {
    const postsWithEmojis = posts.map(postWithEmojis);

    state.lookupTable = {
      ...state.lookupTable,
      ...keyBy(postsWithEmojis, 'post_id'),
    };
  },

  ADD_POST_TO_LOOKUP(state, post) {
    state.lookupTable[post.post_id] = postWithEmojis(post);
    state.lookupTable = { ...state.lookupTable };
  },

  SET_POST_MODAL_SHOWN(state, shown) {
    state.postModalShown = shown;
  },

  SET_POST_MODAL_POST(state, payload) {
    state.postModalPost = payload;
  },

  CLEAR_POST_MODAL_POST() {
    state.postModalPost = null;
  },

  SET_IS_LOADING_POSTS(state, { key, value }) {
    state.isLoading[key] = value;
  },

  /**
   * Set loading flags for specific posts by post_id,
   * so the playlist can show a skeleton while they load.
   */
  SET_POSTS_LOADING(state, { ids, loading }) {
    state.isLoadingMap = {
      ...state.isLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_POSTS_NOT_FOUND(state, { ids, notFound }) {
    state.notFoundMap = {
      ...state.notFoundMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: notFound,
      }), {}),
    };
  },

  ADD_TAGBOARD_POSTS(state, payload) {
    const { tagboardId } = payload;
    const posts = (state.byTagboard[tagboardId] || []).concat(payload.posts);
    state.byTagboard[tagboardId] = posts;
  },

  CLEAR_TAGBOARD_POSTS(state, tagboardId) {
    state.byTagboard[tagboardId] = [];
  },
};

const getters = {
  /**
   * Get social post that is currently live, if there is one.
   * @returns {Object|null}
   */
  getLiveSocialPost(state, getters, rootState) {
    // Get the production
    const { production } = rootState.production;
    if (!production) { return null; }

    // Get the timeline and current live IDs
    const { timeline, index, sub_index: subIndex } = production;
    if (!index || !subIndex) { return null; }

    // Get the current timeline item
    const item = timeline.find((it) => it.timeline_id === index);
    if (!item) { return null; }

    // Get posts for the timeline item.
    // Will pull from nested fields if it's a social graphic.
    const posts = getSocialPosts(item);

    // Lookup post by timeline_id
    const post = posts.find((post) => post.timeline_id === subIndex);
    if (!post) { return null; }

    // And finally look that post up in the lookup table
    return state.lookupTable[post.post_id] || null;
  },

  /**
   * Check current live element's fields for social posts,
   * and return tagboard if found.
   * @returns {Object|null} Tagboard
   */
  getLiveTagboard(state, getters, rootState) {
    // Get the production
    const { production } = rootState.production;
    if (!production) { return null; }

    // Get the timeline and current live IDs
    const { timeline, index, sub_index: subIndex } = production;
    if (!index || !subIndex) { return null; }

    // Get the current timeline item
    const item = timeline.find((it) => it.timeline_id === index);
    if (!item) { return null; }

    // Get posts for the timeline item.
    // Will pull from nested fields if it's a social graphic.
    const posts = getSocialPosts(item);

    const [post] = posts;
    return post?.type === 'tagboard' ? post : null;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
